/**
 * HierarchyTreeNode — shared tenant-tree visualization.
 *
 * Used by AdminPage (group_admin / admin views) and by the Super Admin
 * page's Tenant Tree tab. Lives in components/admin/ so both module
 * pages can pull it without one importing from the other.
 *
 * Single source of truth for the tree styling and behaviour: collapsing,
 * level icons, selection state. Both consumers feed it the same node
 * shape (HierarchyTreeNode from group_admin/types) and get back the same
 * visual language.
 */

import type { HierarchyTreeNode as HierarchyTreeNodeType } from '@modules/group_admin/types';
import { useLanguage } from '@/hooks/useLanguage';
import { tenantDisplayName } from '@/utils/tenantName';
import './HierarchyTreeNode.css';

// Level-based styling for tree nodes — M² brand palette (no purple).
// Index = depth from the root the consumer hands us; deeper levels reuse
// the last entry.
export const levelStyles = [
  { color: '#3DA8D4', bgColor: 'rgba(61, 168, 212, 0.12)', icon: 'crown' },     // Level 0 - Root
  { color: '#6CC7EF', bgColor: 'rgba(108, 199, 239, 0.12)', icon: 'building' }, // Level 1 - Organization
  { color: '#8BBF2A', bgColor: 'rgba(139, 191, 42, 0.12)', icon: 'folder' },    // Level 2 - Department
  { color: '#FAB600', bgColor: 'rgba(250, 182, 0, 0.12)', icon: 'users' },      // Level 3 - Team
  { color: '#D50032', bgColor: 'rgba(213, 0, 50, 0.10)', icon: 'user' },        // Level 4+ - Sub-team
];

// Look up the right SVG for a depth, tinted with the level's accent colour.
export function getLevelIcon(level: number, color: string): JSX.Element {
  const icons: Record<string, JSX.Element> = {
    crown: (
      <svg viewBox="0 0 24 24" width="14" height="14" style={{ marginInlineEnd: '4px', color }}>
        <path d="M2 20h20v2H2zm2-2L1 7l5 3 6-8 6 8 5-3-3 13H4z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      </svg>
    ),
    building: (
      <svg viewBox="0 0 24 24" width="14" height="14" style={{ marginInlineEnd: '4px', color }}>
        <path d="M3 21h18M9 8h1M9 12h1M9 16h1M14 8h1M14 12h1M14 16h1M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16" fill="none" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
    folder: (
      <svg viewBox="0 0 24 24" width="14" height="14" style={{ marginInlineEnd: '4px', color }}>
        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" fill="none" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
    users: (
      <svg viewBox="0 0 24 24" width="14" height="14" style={{ marginInlineEnd: '4px', color }}>
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" fill="none" stroke="currentColor" strokeWidth="2" />
        <circle cx="9" cy="7" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
    user: (
      <svg viewBox="0 0 24 24" width="14" height="14" style={{ marginInlineEnd: '4px', color }}>
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" fill="none" stroke="currentColor" strokeWidth="2" />
        <circle cx="12" cy="7" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
  };
  const style = levelStyles[Math.min(level, levelStyles.length - 1)];
  return icons[style.icon] || icons.user;
}

interface HierarchyTreeNodeProps {
  node: HierarchyTreeNodeType;
  onSelect?: (tenantId: string, tenantName: string, hasChildren: boolean, isRoot: boolean) => void;
  selectedTenantId?: string;
  level?: number;
  collapsedNodes?: Set<string>;
  onToggleCollapse?: (tenantId: string) => void;
  /**
   * Optional per-node badge slot rendered between the tenant name and the
   * user-count pill. The Super Admin Tenant Tree uses it for cascade /
   * module / wiki markers; consumers that don't pass it (AdminPage) are
   * unaffected.
   */
  renderBadges?: (tenantId: string) => React.ReactNode;
}

export function HierarchyTreeNode({
  node,
  onSelect,
  selectedTenantId,
  level = 0,
  collapsedNodes,
  onToggleCollapse,
  renderBadges,
}: HierarchyTreeNodeProps) {
  const { t } = useLanguage();
  const isSelected = selectedTenantId === node.tenant_id;
  const style = levelStyles[Math.min(level, levelStyles.length - 1)];
  const hasChildren = node.has_children ?? (node.children && node.children.length > 0);
  const isCollapsed = collapsedNodes?.has(node.tenant_id) ?? false;

  return (
    <div className="tree-node">
      <div className="tree-node-collapsible">
        {hasChildren && onToggleCollapse && (
          <div
            className="tree-node-toggle"
            onClick={(e) => {
              e.stopPropagation();
              onToggleCollapse(node.tenant_id);
            }}
            title={isCollapsed ? t('common.expand') : t('common.collapse')}
          >
            {isCollapsed ? '▶' : '▼'}
          </div>
        )}
        {(!hasChildren || !onToggleCollapse) && <div style={{ width: 20 }} />}

        <div
          className={`tree-node-content tree-node-level-${Math.min(level, 4)} ${isSelected ? 'tree-node-selected' : ''}`}
          // Stable hooks for tests and tooling — the row shows the NAME, and
          // names are neither unique nor stable, so expose the identity too.
          data-tenant-id={node.tenant_id}
          data-tenant-code={node.tenant_code}
          onClick={() => onSelect?.(node.tenant_id, node.tenant_name, !!hasChildren, !!node.is_root_tenant)}
          style={{ cursor: onSelect ? 'pointer' : 'default', borderInlineStartColor: style.color, flex: 1 }}
        >
          {getLevelIcon(level, style.color)}
          <span className="tree-node-name">{tenantDisplayName(t, node)}</span>
          {renderBadges?.(node.tenant_id)}
          <span className="tree-node-users" style={{ background: style.bgColor, color: style.color }}>
            <svg viewBox="0 0 24 24" width="12" height="12" style={{ marginInlineEnd: '3px' }}>
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" fill="none" stroke="currentColor" strokeWidth="2" />
              <circle cx="12" cy="7" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
            </svg>
            {node.user_count ?? 0}
            {node.total_users_with_descendants !== undefined && node.total_users_with_descendants > (node.user_count ?? 0) && (
              <span style={{ marginInlineStart: '3px', opacity: 0.7, fontSize: '0.9em' }}>
                ({node.total_users_with_descendants})
              </span>
            )}
          </span>
        </div>
      </div>
      {node.children && node.children.length > 0 && !isCollapsed && (
        <div className="tree-children">
          {node.children.map((child) => (
            <HierarchyTreeNode
              key={child.tenant_id}
              node={child}
              onSelect={onSelect}
              selectedTenantId={selectedTenantId}
              level={level + 1}
              collapsedNodes={collapsedNodes}
              onToggleCollapse={onToggleCollapse}
              renderBadges={renderBadges}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default HierarchyTreeNode;
